import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Grid, Snackbar, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { boxApi } from '@/services/box.api';
import { LoadingState } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { BoxConfigurationCard } from '@/components/box/BoxConfigurationCard';
import { DeleteConfigurationDialog } from '@/components/configurations/DeleteConfigurationDialog';
import type { BoxConfiguration } from '@/types';

/** Limite di configurazioni box attive (una copre tutti i box del progetto). */
const BOX_CONFIG_LIMIT = 4;

/**
 * "Box auto — le tue configurazioni": lista delle configurazioni box (CRUD) con avanzamento.
 * Sezione originale (non legacy). Consuma GET /box/configurations.
 */
export default function BoxConfigurationsPage() {
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState<BoxConfiguration[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = (): void => {
    setIsLoading(true);
    setError(null);
    boxApi
      .getConfigurations()
      .then((res) => {
        setConfigurations(res.configurations);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Impossibile caricare le configurazioni box. Riprova.');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = (): void => {
    boxApi
      .createConfiguration()
      .then((res) => navigate(`/box/${res.configuration.id}`))
      .catch(() => setToast('Impossibile creare la configurazione box. Riprova.'));
  };

  const handleRename = (id: number, name: string): void => {
    boxApi
      .renameConfiguration(id, name)
      .then(() => load())
      .catch(() => setToast('Impossibile rinominare la configurazione. Riprova.'));
  };

  const handleDeleteConfirm = (): void => {
    if (deleteId == null) return;
    const id = deleteId;
    setDeleteId(null);
    boxApi
      .deleteConfiguration(id)
      .then(() => load())
      .catch(() => setToast('Impossibile eliminare la configurazione. Riprova.'));
  };

  const isFull = (configurations?.length ?? 0) >= BOX_CONFIG_LIMIT;

  return (
    <Box sx={{ flex: 1, px: { xs: 2, md: 6 }, py: 5 }}>
      <Button
        variant="text"
        color="inherit"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/apartments')}
        sx={{ mb: 1, pl: 0 }}
      >
        Torna alla home
      </Button>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 4 }}>
        <Box>
          <Typography variant="h2" gutterBottom>
            Box auto
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Personalizza i tuoi box: basculante, ricarica auto ed eventuali unioni.
          </Typography>
        </Box>
        {!isLoading && !error && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            disabled={isFull}
            onClick={handleCreate}
          >
            Nuova configurazione
          </Button>
        )}
      </Box>

      {isLoading && <LoadingState message="Caricamento configurazioni box…" />}
      {error && <ErrorState message={error} onRetry={load} />}

      {!isLoading && !error && configurations && configurations.length === 0 && (
        <Typography variant="body1" color="text.secondary">
          Non hai ancora configurazioni box. Creane una per iniziare.
        </Typography>
      )}

      {!isLoading && !error && configurations && configurations.length > 0 && (
        <Grid container spacing={3}>
          {configurations.map((configuration, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={configuration.id}>
              <BoxConfigurationCard
                configuration={configuration}
                index={i + 1}
                onOpen={(id) => navigate(`/box/${id}`)}
                onRename={handleRename}
                onDelete={(id) => setDeleteId(id)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {isFull && !isLoading && !error && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Hai raggiunto il limite di {BOX_CONFIG_LIMIT} configurazioni box.
        </Typography>
      )}

      <DeleteConfigurationDialog
        open={deleteId != null}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
      />

      <Snackbar
        open={toast != null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
